/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */

import React, { useEffect, useState } from "react";
import Author from "components/Table/Author";
import MDBox from "components/MDBox";
import { IconButton, Tooltip } from "@mui/material";
import { Icons, defaultData } from "utils/Constants";
import moment from "moment";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

export const inquiryStatusStyles = {
  pending: { backgroundColor: "#FEF5CD", color: "#DA850A" },
  processed: { backgroundColor: "#DBF4DB", color: "#28873C" },
  rejected: { backgroundColor: "#FDE8E8", color: "#E02424" },
  "rejected by bank": { backgroundColor: "#FDE8E8", color: "#D92D20" },
  "denied by customer": { backgroundColor: "#FEF0C7", color: "#B54708" },
  default: { backgroundColor: "#F2F4F7", color: "#344054" },
};

const renderInquiryStatus = (status, rejectionReason) => {
  const normalizedStatus = (status || "").toLowerCase().trim();
  let label = normalizedStatus;
  let styleKey = normalizedStatus;

  if (normalizedStatus === "rejected by bank") {
    label = "Rejected by Bank";
    styleKey = "rejected by bank";
  } else if (normalizedStatus === "denied by customer") {
    label = "Denied by Customer";
    styleKey = "denied by customer";
  } else if (normalizedStatus === "rejected") {
    const reasonLower = (rejectionReason || "").toLowerCase();
    if (reasonLower.includes("bank")) {
      label = "Rejected by Bank";
      styleKey = "rejected by bank";
    } else if (reasonLower.includes("customer") || reasonLower.includes("denied")) {
      label = "Denied by Customer";
      styleKey = "denied by customer";
    } else {
      label = "Rejected";
      styleKey = "rejected";
    }
  } else if (normalizedStatus.length > 0) {
    label = normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1);
  } else {
    label = "-";
  }

  const statusStyle = inquiryStatusStyles[styleKey] || inquiryStatusStyles.default;

  return (
    <MDBox
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 24,
        px: 1.25,
        py: 0.25,
        borderRadius: "6px",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.4,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
        ...statusStyle,
      }}
    >
      {label || "-"}
    </MDBox>
  );
};

export const LOAN_TYPE_LABELS = {
  home: "Home Loan",
  personal: "Personal Loan",
  business: "Business Loan",
  car: "Car Loan",
  education: "Education Loan",
  credit: "Credit Card",
  "credit card": "Credit Card",
  loanAgainstProperty: "Loan Against Property",
  loanAgainstSecurity: "Loan Against Security",
  workingCapital: "Working Capital",
  machinery: "Machinery Loan",
  gold: "Gold Loan",
};

export const getLoanTypeLabel = (type) => {
  if (!type) return "-";
  return LOAN_TYPE_LABELS[type] || type;
};

const formatDateValue = (date) => {
  if (!date) return "-";
  return moment(date).format(defaultData.WEB_DATE_FORMAT);
};

const getProcessTooltipTitle = (isPending, isProcessed) => {
  if (isPending) return "Process / Apply for Loan";
  if (isProcessed) return "Inquiry already processed / Loan form submitted";
  return "Cannot process rejected inquiry";
};

const getEditTooltipTitle = (isPending) => {
  if (isPending) return "Edit Inquiry";
  return "Only pending inquiries can be edited";
};

export default function inquiriesTableData(
  inquiriesData,
  handleProcessModal,
  handleRejectModal,
  handleDeleteInquiry,
  handleEditModal,
  isAdmin = true
) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (inquiriesData) {
      const list = inquiriesData.map((item, index) => {
        const statusLower = (item.status || "").toLowerCase().trim();
        const isPending = statusLower === "pending";
        const isProcessed = statusLower === "processed";

        return {
          srNo: <Author name={index + 1} />,
          leadDate: <Author name={formatDateValue(item?.leadDate || item?.createdAt)} />,
          inquiryId: <Author name={item.inquiryId ? `INQ-${item.inquiryId}` : "-"} />,
          name: <Author name={`${item.firstName || ""} ${item.lastName || ""}`.trim() || "-"} />,
          mobileNumber: <Author name={item.mobileNumber || "-"} />,
          imd: <Author name={item.imd || "-"} />,
          salesManager: (
            <Author
              name={
                item.salesManager?.sm ||
                (typeof item.salesManager === "string" ? item.salesManager : "-")
              }
            />
          ),
          loanType: <Author name={getLoanTypeLabel(item.loanType)} />,
          loanAmount: (
            <Author
              name={
                item.loanAmount
                  ? `₹${Number(item.loanAmount.toString().replace(/,/g, "")).toLocaleString(
                      "en-IN"
                    )}`
                  : "-"
              }
              style={{ textAlign: "right" }}
              align="right"
            />
          ),
          status: renderInquiryStatus(item.status, item.rejectionReason),
          rejectionReason: (
            <Author name={item.rejectionReason || "-"} title={item.rejectionReason || ""} />
          ),
          action: (
            <MDBox display="flex" alignItems="center" gap={0.5}>
              <Tooltip title={getEditTooltipTitle(isPending)}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!isPending}
                    sx={{
                      color: isPending ? "#1570EF" : "#D0D5DD",
                      "&:hover": { backgroundColor: isPending ? "#EFF8FF" : "transparent" },
                      "&.Mui-disabled": { color: "#D0D5DD", cursor: "not-allowed" },
                    }}
                    onClick={() => isPending && handleEditModal && handleEditModal(item)}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={getProcessTooltipTitle(isPending, isProcessed)}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!isPending}
                    sx={{
                      color: isPending ? "#28873C" : "#D0D5DD",
                      "&:hover": { backgroundColor: isPending ? "#DBF4DB" : "transparent" },
                      "&.Mui-disabled": { color: "#D0D5DD", cursor: "not-allowed" },
                    }}
                    onClick={() => isPending && handleProcessModal(item)}
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {isAdmin && (
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    sx={{
                      color: "#475467",
                      "&:hover": { backgroundColor: "#F2F4F7" },
                    }}
                    onClick={() => handleDeleteInquiry(item)}
                  >
                    {Icons.DELETE}
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          ),
        };
      });

      setRows(list);
    }
  }, [inquiriesData, isAdmin]);

  return {
    columns: [
      { Header: "No.", accessor: "srNo", width: "4%" },
      { Header: "Lead Date", accessor: "leadDate", width: "9%" },
      { Header: "Inquiry ID", accessor: "inquiryId", width: "8%" },
      { Header: "Name", accessor: "name", width: "12%" },
      { Header: "Mobile Number", accessor: "mobileNumber", width: "10%" },
      { Header: "IMD", accessor: "imd", width: "7%" },
      { Header: "Sales Manager", accessor: "salesManager", width: "11%" },
      { Header: "Loan Type", accessor: "loanType", width: "11%" },
      { Header: "Loan Amount", accessor: "loanAmount", width: "10%", align: "right" },
      { Header: "Status", accessor: "status", width: "9%" },
      { Header: "Rejection Reason", accessor: "rejectionReason", width: "12%" },
      { Header: "Action", accessor: "action", width: "9%" },
    ],
    rows,
  };
}
